import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, MapPin, Pencil, Plus, Search, X } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import {
  LEVELS,
  LEVEL_LABELS,
  PARENT_LEVEL,
  createLocation,
  countListingsByCity,
  fetchLocations,
  renameLocation,
  setLocationActive,
  type LocationLevel,
  type LocationRow,
} from "@/lib/locations";

export const Route = createFileRoute("/_authenticated/super-admin/cities")({
  head: () => ({
    meta: [
      { title: "City management · Landoo Super Admin" },
      {
        name: "description",
        content:
          "Manage Landoo states, districts, cities, areas and localities. Add, rename or disable locations used by listings.",
      },
      { property: "og:title", content: "City management · Landoo Super Admin" },
      {
        property: "og:description",
        content: "Add, edit and disable the locations Landoo listings can use.",
      },
    ],
  }),
  component: CitiesPage,
});

const inp =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-xs outline-none focus:border-brand";

function CitiesPage() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["locations"],
    queryFn: fetchLocations,
  });
  const { data: usage = {} } = useQuery({
    queryKey: ["locations", "usage"],
    queryFn: countListingsByCity,
  });

  const [level, setLevel] = useState<LocationLevel>("city");
  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const parentLevel = PARENT_LEVEL[level];
  const parentOptions = useMemo(
    () => (parentLevel ? rows.filter((r) => r.level === parentLevel && r.active) : []),
    [rows, parentLevel],
  );

  const pathOf = (row: LocationRow): string => {
    const parts: string[] = [];
    let cur: LocationRow | undefined = row.parent_id ? byId.get(row.parent_id) : undefined;
    let guard = 0;
    while (cur && guard++ < 6) {
      parts.unshift(cur.name);
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    }
    return parts.join(" › ");
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => r.level === level)
      .filter((r) => !q || r.name.toLowerCase().includes(q) || pathOf(r).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, level, search, byId]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["locations"] });

  const add = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (parentLevel && !parentId) {
      toast.error(`Select the parent ${LEVEL_LABELS[parentLevel].toLowerCase()}`);
      return;
    }
    setBusy(true);
    try {
      await createLocation({ name: name.trim(), level, parent_id: parentLevel ? parentId : null });
      setName("");
      refresh();
      toast.success(`${LEVEL_LABELS[level]} added`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not add";
      toast.error(msg.includes("duplicate") ? "That name already exists here" : msg);
    } finally {
      setBusy(false);
    }
  };

  const saveName = async (id: string) => {
    if (!editName.trim()) return;
    try {
      await renameLocation(id, editName.trim());
      setEditingId(null);
      refresh();
      toast.success("Renamed");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not rename");
    }
  };

  const toggle = async (row: LocationRow) => {
    const used = row.level === "city" ? (usage[row.name.trim().toLowerCase()] ?? 0) : 0;
    try {
      await setLocationActive(row.id, !row.active);
      refresh();
      toast.success(
        row.active
          ? used > 0
            ? `Disabled — ${used} existing listing${used === 1 ? "" : "s"} keep this city, but it can't be chosen for new ones`
            : "Disabled"
          : "Enabled",
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not update");
    }
  };

  return (
    <DashboardShell title="City management" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,340px)_1fr]">
        <section className="rounded-xl border border-border bg-card p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <MapPin className="h-4 w-4 text-primary" /> Add location
          </h2>
          <div className="mt-3 space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-muted-foreground">
                Level
              </span>
              <select
                className={inp}
                value={level}
                onChange={(e) => {
                  setLevel(e.target.value as LocationLevel);
                  setParentId("");
                }}
              >
                {LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {LEVEL_LABELS[l]}
                  </option>
                ))}
              </select>
            </label>
            {parentLevel ? (
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-muted-foreground">
                  Parent {LEVEL_LABELS[parentLevel].toLowerCase()}
                </span>
                <select
                  className={inp}
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                >
                  <option value="">Select…</option>
                  {parentOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {parentOptions.length === 0 ? (
                  <span className="mt-1 block text-[11px] text-muted-foreground">
                    Add an active {LEVEL_LABELS[parentLevel].toLowerCase()} first.
                  </span>
                ) : null}
              </label>
            ) : null}
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-muted-foreground">Name</span>
              <input className={inp} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <button
              type="button"
              disabled={busy}
              onClick={add}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" /> Add {LEVEL_LABELS[level].toLowerCase()}
            </button>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Locations are never deleted — disable them instead. Listings already using a disabled
              city keep their data, but new listings can only pick active cities.
            </p>
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-foreground">{LEVEL_LABELS[level]} list</h2>
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                className={`${inp} pl-8`}
                placeholder={`Search ${LEVEL_LABELS[level].toLowerCase()}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {LEVELS.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLevel(l)}
                className={`rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                  level === l
                    ? "bg-primary text-primary-foreground"
                    : "border border-border bg-background text-muted-foreground hover:bg-accent"
                }`}
              >
                {LEVEL_LABELS[l]}
              </button>
            ))}
          </div>

          {isLoading ? (
            <p className="mt-3 text-xs text-muted-foreground">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="mt-3 text-xs text-muted-foreground">
              No {LEVEL_LABELS[level].toLowerCase()} entries yet.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {visible.map((row) => {
                const used = row.level === "city" ? (usage[row.name.trim().toLowerCase()] ?? 0) : 0;
                return (
                  <li
                    key={row.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
                  >
                    <div className="min-w-0 flex-1">
                      {editingId === row.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            className={inp}
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                          />
                          <button
                            type="button"
                            aria-label="Save name"
                            onClick={() => saveName(row.id)}
                            className="rounded-md p-1.5 text-emerald-600 hover:bg-accent"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            aria-label="Cancel rename"
                            onClick={() => setEditingId(null)}
                            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm font-semibold text-foreground">{row.name}</p>
                          <p className="mt-0.5 text-[11px] text-muted-foreground">
                            {pathOf(row) || "Top level"}
                            {row.level === "city"
                              ? ` · ${used} listing${used === 1 ? "" : "s"}`
                              : ""}
                          </p>
                        </>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => toggle(row)}
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                          row.active
                            ? "bg-emerald-500/10 text-emerald-600"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {row.active ? "Active" : "Disabled"}
                      </button>
                      <button
                        type="button"
                        aria-label="Rename location"
                        onClick={() => {
                          setEditingId(row.id);
                          setEditName(row.name);
                        }}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </DashboardShell>
  );
}
